import React, { useCallback, useEffect, useState, memo, ChangeEvent } from 'react';
import { useHistory } from 'react-router-dom';
import Typography from '@mui/material/Typography';
import {
  Box,
  CssBaseline,
  FormControl,
  LinearProgress,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  ThemeProvider,
  InputLabel,
  TextField
} from '@mui/material';
import Button from '@mui/material/Button';
import { DataGrid } from '@mui/x-data-grid';
import themev5 from '../../themev5';
import Styles from '../Canada/styles/curationdashboard.styles';
import styles from '../Datagrid/Datagrid.styles';
import DatagridFooter from '../Datagrid/DatagridFooter';
import DatagridNoRowsOverlay from '../Datagrid/DatagridNoRowsOverlay';
import { getAllDataQuality } from '../../api/pmda';

const ListTranslationQuality = () => {
  const classes = Styles();

  const history = useHistory();
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [pageData, setPageData] = useState({
    pageNo: 0,
    pageSize: 10
  });

  const [totalRowCount, setTotalRowCount] = useState(0);
  const rowsPerPageOptions = [10, 20, 50, 100];

  const [yjCode, setYjCode] = useState<string | undefined>('');
  const [qcStatus, setQCStatus] = useState<string>('all');

  const searchFields = [
    {
      id: 'yj_code',
      label: 'YJ Code',
      value: yjCode,
      onChange: (value: string) => {
        setYjCode(value);
      }
    },

    {
      id: 'qc_status',
      label: 'Qc status',
      options: ['all', 'qced', 'automated', 'escalate', 'non qced'],
      value: qcStatus,
      onChange: (value: string) => {
        setQCStatus(value);
      }
    }
  ];

  const DATAGRID_COLUMNS: any = [
    {
      field: 'yj_code',
      headerName: 'YJ Code',
      width: 170,
      headerAlign: 'center',
      align: 'center'
    },
    {
      field: 'active_ingredient',
      headerName: 'Generic Name / Active Ingredient',
      width: 350,
      headerAlign: 'center',
      align: 'left'
    },
    {
      field: 'trade_name',
      headerName: 'Drug Name / Trade Name',
      width: 350,
      headerAlign: 'center',
      align: 'left'
    },

    {
      field: 'translation_check_status',
      headerName: 'QC Status',
      width: 200,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params: any) => {
        return <Typography variant='body1'>{params.row.translation_check_status}</Typography>;
      }
    },

    {
      field: 'Edit',
      width: 500,
      headerAlign: 'left',
      align: 'left',
      renderCell: (params: any) => {
        return (
          <Stack direction='row' spacing={2}>
            <Box>
              <Button
                variant={params.row.translation_check_status === 'qced' ? 'outlined' : 'contained'}
                sx={{ textTransform: 'none' }}
                onClick={() => {
                  history.push(`/pmda/quality/translation/${params.row.yj_code}`);
                }}>
                <Typography>Fix data</Typography>
              </Button>
            </Box>
          </Stack>
        );
      }
    }
  ];

  const fetchData = useCallback(async () => {
    try {
      setIsLoading(true);

      const response = await getAllDataQuality(
        pageData.pageSize,
        pageData.pageNo,
        yjCode,
        qcStatus
      );
      if (response === undefined) {
        throw new Error('Result is undefined');
      }
      if (response.data && response.data.body.data) {
        setIsLoading(false);
        const resultData = response.data.body.data;
        setData(resultData);
        setTotalRowCount(response.data.body.count);
        localStorage.setItem(
          'pmdaDataQualityYjCodeList',
          JSON.stringify(response.data.body.yjCodeList)
        );
      } else {
        setIsLoading(false);
        throw Error('No data');
      }
    } catch (e: any) {
      throw new Error(`Error ${e}`);
    } finally {
      setIsLoading(false);
    }
  }, [pageData]);

  useEffect(() => {
    fetchData();
  }, [pageData]);

  return (
    <ThemeProvider theme={themev5}>
      <CssBaseline />

      <Box display='flex' ml={2} mt={10} alignItems='center'>
        {searchFields.map(row => {
          if ('options' in row) {
            return (
              <FormControl key={row.id}>
                <InputLabel id={`${row.id}-label`}>{row.label}</InputLabel>
                <Select
                  labelId={`${row.id}-label`}
                  id={row.id}
                  value={row.value}
                  label={row.label}
                  sx={{ p: '0', minWidth: '250px', mr: '20px' }}
                  onChange={(event: SelectChangeEvent) => {
                    row.onChange(event.target.value as string);
                  }}>
                  {row?.options?.map((option: string) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            );
          }
          return (
            <TextField
              key={row.id}
              variant='outlined'
              type='search'
              id={row.id}
              label={row.label}
              value={row.value}
              sx={{ paddingRight: '30px' }}
              InputProps={{
                style: { fontSize: '1.5vh', minWidth: '250px', paddingRight: '20px' }
              }}
              InputLabelProps={{
                style: { fontSize: '1.5vh' }
              }}
              onChange={(event: ChangeEvent<HTMLInputElement>) => {
                row.onChange(event.target.value);
              }}
            />
          );
        })}
        <Button
          onClick={() => {
            setPageData({ pageNo: 0, pageSize: pageData.pageSize });
          }}
          variant='contained'
          sx={{ textTransform: 'none' }}>
          <Typography>Search</Typography>
        </Button>
      </Box>
      <Box
        height='80vh'
        p={2}
        mt={10}
        display='flex'
        alignItems='center'
        justifyContent='center'
        className={classes.root}>
        <DataGrid
          sx={styles.datagrid}
          style={{ height: '100%' }}
          rowSpacingType='margin'
          sortingOrder={['desc', 'asc']}
          checkboxSelection={false}
          density='standard'
          rows={data}
          rowCount={totalRowCount}
          columns={DATAGRID_COLUMNS}
          loading={isLoading}
          getRowId={(row: any) => row.yj_code}
          paginationMode='server'
          getRowClassName={() => `table-rows`}
          pageSize={pageData.pageSize}
          page={pageData.pageNo}
          onPageSizeChange={newPageSize => {
            setPageData({ pageNo: 0, pageSize: newPageSize });
          }}
          onPageChange={number => {
            setPageData(prevState => ({ ...prevState, pageNo: number }));
          }}
          rowsPerPageOptions={rowsPerPageOptions}
          components={{
            LoadingOverlay: LinearProgress,
            Footer: DatagridFooter,
            NoRowsOverlay: DatagridNoRowsOverlay
          }}
          pagination
        />
      </Box>
    </ThemeProvider>
  );
};
export default memo(ListTranslationQuality);
